import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Calendar as CalendarIcon, Clock, Users, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { FadeIn, SlideIn } from '../components/PageTransition';
import plate from "../assets/plate.jpg"
export const ReservationsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '',
    occasion: '',
    specialRequests: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

const handleSubmit = (e) => {
  e.preventDefault();

  const message = `
New Reservation Request

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Date: ${formData.date}
Time: ${formData.time}
Guests: ${formData.guests}
Occasion: ${formData.occasion}
Special Requests: ${formData.specialRequests}
  `;

  // Replace with your WhatsApp number
  const whatsappNumber = "+918484875750";

  const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

  window.open(whatsappURL, "_blank");

  toast.success(
    "Reservation request submitted! We will confirm shortly."
  );

  setFormData({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '',
    occasion: '',
    specialRequests: ''
  });
};

  const timeSlots = [
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
    '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
    '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM',
    '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM', '10:00 PM'
  ];

  const occasions = [
    'Birthday', 'Anniversary', 'Business Meeting',
    'Family Gathering', 'Date Night', 'Other'
  ];

  const benefits = [
    { icon: CheckCircle, text: 'Priority Seating' },
    { icon: CheckCircle, text: 'Special Arrangements' },
    { icon: CheckCircle, text: 'Customized Service' },
    { icon: CheckCircle, text: 'No Waiting Time' }
  ];

  return (
    <div className="min-h-screen mt-[95px]">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
        >
          <img
            src={plate}
            alt="Restaurant"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
        </motion.div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Reserve Your{' '}
              <span className="text-amber-400">Perfect Table</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-10 leading-relaxed">
              Book your table in advance and enjoy a seamless dining experience
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <FadeIn>
        <section className="py-16 bg-amber-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-3 text-white"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <benefit.icon className="w-6 h-6 text-amber-300" />
                  <span className="font-medium">{benefit.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </FadeIn>

      {/* Reservation Form Section */}
      <section className="py-20 bg-gradient-to-b from-white to-amber-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <FadeIn>
              <div className="text-center mb-12">
                <h2 className="text-4xl sm:text-5xl font-bold text-amber-900 mb-4">
                  Make a Reservation
                </h2>
                <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
                <p className="text-lg text-gray-600">
                  Fill in the details below and we'll confirm your reservation
                </p>
              </div>
            </FadeIn>

            <SlideIn direction="up">
              <Card className="border-amber-100 shadow-2xl">
                <CardContent className="p-8 md:p-12">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Personal Information */}
                    <motion.div
                      className="space-y-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <h3 className="text-xl font-bold text-amber-900 mb-4">
                        Personal Information
                      </h3>
                      <Input
                        name="name"
                        placeholder="Full Name *"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="border-amber-200 focus:border-amber-500 py-6"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          name="email"
                          type="email"
                          placeholder="Email Address *"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="border-amber-200 focus:border-amber-500 py-6"
                        />
                        <Input
                          name="phone"
                          type="tel"
                          placeholder="Phone Number *"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="border-amber-200 focus:border-amber-500 py-6"
                        />
                      </div>
                    </motion.div>

                    {/* Reservation Details */}
                    <motion.div
                      className="space-y-4 pt-6 border-t border-amber-200"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <h3 className="text-xl font-bold text-amber-900 mb-4 flex items-center gap-2">
                        <CalendarIcon className="w-5 h-5" />
                        Reservation Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Date *
                          </label>
                          <Input
                            name="date"
                            type="date"
                            value={formData.date}
                            onChange={handleChange}
                            required
                            min={new Date().toISOString().split('T')[0]}
                            className="border-amber-200 focus:border-amber-500 py-6"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Time *
                          </label>
                          <select
                            name="time"
                            value={formData.time}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-3 border border-amber-200 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="">Select Time</option>
                            {timeSlots.map((slot) => (
                              <option key={slot} value={slot}>
                                {slot}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Guests *
                          </label>
                          <Input
                            name="guests"
                            type="number"
                            placeholder="Number of guests"
                            value={formData.guests}
                            onChange={handleChange}
                            required
                            min="1"
                            max="20"
                            className="border-amber-200 focus:border-amber-500 py-6"
                          />
                        </div>
                      </div>
                    </motion.div>

                    {/* Additional Information */}
                    <motion.div
                      className="space-y-4 pt-6 border-t border-amber-200"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <h3 className="text-xl font-bold text-amber-900 mb-4">
                        Additional Information
                      </h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Occasion (Optional)
                        </label>
                        <select
                          name="occasion"
                          value={formData.occasion}
                          onChange={handleChange}
                          className="w-full px-3 py-3 border border-amber-200 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">Select Occasion</option>
                          {occasions.map((occasion) => (
                            <option key={occasion} value={occasion}>
                              {occasion}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Special Requests (Optional)
                        </label>
                        <Textarea
                          name="specialRequests"
                          placeholder="Any dietary restrictions, seating preferences, or special arrangements..."
                          value={formData.specialRequests}
                          onChange={handleChange}
                          rows={4}
                          className="border-amber-200 focus:border-amber-500"
                        />
                      </div>
                    </motion.div>

                    {/* Submit Button */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="pt-6"
                    >
                      <Button
                        type="submit"
                        className="w-full bg-amber-700 hover:bg-amber-800 text-white py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105"
                      >
                        <Send className="w-5 h-5 mr-2" />
                        Confirm Reservation
                      </Button>
                     
                    </motion.div>
                  </form>
                </CardContent>
              </Card>
            </SlideIn>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <FadeIn>
        {/* <section className="py-16 bg-amber-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h3 className="text-2xl font-bold text-amber-900 mb-6 text-center">
                Reservation Policy
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <Clock className="w-8 h-8 text-amber-700 mx-auto mb-3" />
                  <h4 className="font-semibold text-amber-900 mb-2">Arrival Time</h4>
                  <p className="text-gray-600 text-sm">
                    Please arrive within 15 minutes of your reservation time
                  </p>
                </div>
                <div>
                  <Users className="w-8 h-8 text-amber-700 mx-auto mb-3" />
                  <h4 className="font-semibold text-amber-900 mb-2">Group Size</h4>
                  <p className="text-gray-600 text-sm">
                    For parties over 10, please call us directly
                  </p>
                </div>
                <div>
                  <CalendarIcon className="w-8 h-8 text-amber-700 mx-auto mb-3" />
                  <h4 className="font-semibold text-amber-900 mb-2">Cancellation</h4>
                  <p className="text-gray-600 text-sm">
                    Free cancellation up to 2 hours before reservation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section> */}
      </FadeIn>
    </div>
  );
};

export default ReservationsPage;
